import { sanitizeCountryCode } from "@expert/common-ui";
import { Popover } from "@mantine/core";
import { type JSX, useMemo } from "react";

import { TransferCall } from "@/call-controls/TransferCall/TransferCall";
import { IconButton, Plus } from "@/common-ui";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { isVoiceTask, useActiveTask } from "@/sdk";

import { useControlsStore } from "../controls.store";

interface TransferButtonProps {
    disabled?: boolean;
}

export function ToggleTransferPanelButton({ disabled = false, ...props }: TransferButtonProps): JSX.Element {
    const transferCallActive = useControlsStore((state) => state.transferCallActive);
    const tooltipLabel = !disabled ? "Add and transfer" : "Max participants reached";
    const activeTask = useActiveTask();
    const voiceTask = useMemo(() => {
        return isVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);
    const {
        features: {
            countrySelectEnabled = false,
            clientEnabledCountries = ["US"],
            isVdnTransferInputEnabled = false,
            defaultCountry = "US",
            isSegmentedTransferMenuEnabled = false,
            isInternationalPhoneEnabled = false,
        },
    } = usePartnerConfig();
    const sanitizedDefaultCountry = sanitizeCountryCode(defaultCountry);
    const sanitizedClientEnabledCountries = clientEnabledCountries.map(sanitizeCountryCode);

    return (
        <Popover
            opened={transferCallActive}
            position="bottom"
            zIndex="var(--mantine-priority-highest)"
            styles={{
                dropdown: {
                    backgroundColor: "transparent",
                    border: "none",
                    padding: 0,
                },
            }}
        >
            <Popover.Target>
                <IconButton
                    active={transferCallActive}
                    analytics={{
                        actionId: "TransferCallButton",
                    }}
                    data-testid="call-controls-transfer"
                    disabled={disabled}
                    onClick={useControlsStore.getState().toggleTransferActive}
                    radius="xl"
                    size="30"
                    aria-label="Add and transfer participants"
                    tooltip={tooltipLabel}
                    style={{
                        backgroundColor: transferCallActive ? "var(--mantine-color-white)" : "transparent",
                    }}
                    {...props}
                >
                    <Plus
                        height={14}
                        stroke={transferCallActive ? "var(--mantine-color-dark-7)" : "var(--mantine-color-gray-0)"}
                    />
                </IconButton>
            </Popover.Target>
            <Popover.Dropdown>
                {transferCallActive && voiceTask ? (
                    <TransferCall
                        voiceTask={voiceTask}
                        isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                        defaultCountry={sanitizedDefaultCountry}
                        clientEnabledCountries={sanitizedClientEnabledCountries}
                        isVdnTransferInputEnabled={isVdnTransferInputEnabled}
                        isSegmentedTransferMenuEnabled={isSegmentedTransferMenuEnabled}
                        countrySelectEnabled={countrySelectEnabled}
                    />
                ) : null}
            </Popover.Dropdown>
        </Popover>
    );
}
